import Image from "next/image";
import { useMemo, useState } from "react";

import { CardCategoryEnum, VariantFinishEnum } from "@sorcery/db";
import { useBreakpoints } from "@sorcery/ui";

import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from "~/utils/card";
import { CardFlipper } from "./CardFlipper";

type Props = {
  card?: {
    id: string;
    name: string;
    guardian: {
      category: CardCategoryEnum | null;
    } | null;
    reverse: {
      id: string;
      name: string;
      guardian: {
        category: CardCategoryEnum | null;
      } | null;
    } | null;
  };
  variant?: {
    id: string;
    src: string;
    finish: VariantFinishEnum;
    reverse: {
      id: string;
      src: string;
      finish: VariantFinishEnum;
    } | null;
  };
};

export const CardSquiggly = ({ card, variant }: Props) => {
  const { isLg } = useBreakpoints();
  const style: React.CSSProperties = useMemo(() => {
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    return card?.guardian!.category === CardCategoryEnum.Site
      ? {
          transform: "scale(0.715) rotate(90deg) translate(-47.8%, 0)",
        }
      : {};
  }, [card?.guardian]);

  // rotation qurik
  const h = useMemo(() => {
    return (
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      (card?.guardian!.category === CardCategoryEnum.Site
        ? DEFAULT_CARD_WIDTH / 1.3
        : DEFAULT_CARD_HEIGHT / 1.3) * (!isLg ? 0.8 : 1)
    );
  }, [card?.guardian, isLg]);
  const w = useMemo(() => {
    return (
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      (card?.guardian!.category === CardCategoryEnum.Site
        ? DEFAULT_CARD_HEIGHT / 1.3
        : DEFAULT_CARD_WIDTH / 1.3) * (!isLg ? 0.8 : 1)
    );
  }, [card?.guardian, isLg]);

  const hasReverse = !!card?.reverse;
  const [isReversed, setIsReversed] = useState(false);

  const toggleVariant = () => {
    setIsReversed(!isReversed);
  };

  const displayedVariant = isReversed && hasReverse ? variant?.reverse : variant;

  return (
    // sticky container
    <div className="sticky top-6 max-h-[97vh]">
      {/* card img container */}
      <div
        className={`flex flex-row overflow-hidden ${
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          card?.guardian!.category === CardCategoryEnum.Site ? "justify-center" : "justify-end"
        } animate-in fade-in`}
        // squigglies rotate so need to reserve the highest of the 4 magic numbers for consistent sizing
        style={{
          height: Math.max(h, w),
          width: Math.max(h, w),
        }}
      >
        {card && variant && displayedVariant && (
          <div className="relative duration-200 animate-in fade-in" key={displayedVariant.id}>
            <Image height={h} width={w} alt={card.name} style={style} src={displayedVariant.src} />
            {/* reverse flipper */}
            {hasReverse && <CardFlipper onFlip={toggleVariant} />}
            {variant.finish === VariantFinishEnum.Foil && (
              <div className="absolute left-0 top-0 z-10 opacity-30 duration-500 animate-in fade-in">
                <div
                  className="rounded-[22px]"
                  style={{
                    height: h,
                    width: w,
                    background:
                      "repeating-linear-gradient(-45deg,rgba(233, 238, 82, 0.5),rgba(231, 60, 126, 0.5),rgba(35, 166, 213, 0.5),rgba(35, 213, 171, 0.5))",
                    backgroundSize: "400% 400%",
                    backgroundPosition: "0 0",
                    animation: "sparkle 5s ease-in-out infinite",
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
